import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  HStack,
  IconButton,
  Button,
  Textarea,
  VStack,
  Image,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { pxToRem } from "../../utils/editor.ts";
import { PaperService } from "../../services/paperService";
import { BlockEditor } from "@fk/editor";
import "@fk/editor/dist/editor.css";
import { convertOutlineToTiptapJson } from "../../utils/tiptapConverter.ts";
import useOutlineStore from "../../stores/modules/outline.ts";
import { cloneDeep } from "lodash";

interface AutoGenModalProps {
  isOpen: boolean;
  onClose: () => void;
  paperId: string;
  type?: 'abstract' | 'summary' | 'conclusion';
}

const AutoGenModal: React.FC<AutoGenModalProps> = ({
  isOpen,
  onClose,
  paperId,
  type = 'abstract',
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [summaryPrompt, setSummaryPrompt] = useState("");
  const [carouselContent, setCarouselContent] = useState<
    Array<{ content: any }>
  >([]);
  const [originalNodes, setOriginalNodes] = useState<Array<any>>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  // 添加动画相关状态
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right' | null>(null);

  // 直接在AutoGenModal中处理插入到文章的功能
  const handleInsertToArticle = () => {
    if (originalNodes.length === 0 || currentSlide >= originalNodes.length)
      return;

    const selectedNode = originalNodes[currentSlide];
    const { outline, setOutline, setOutlineIsChange, setCurrentSectionIndex } =
      useOutlineStore.getState();

    if (!outline || !selectedNode) return;

    // 创建outline的深拷贝
    const newOutline = cloneDeep(outline);

    // 根据type决定节点插入位置
    if (newOutline.nodes) {
      if (type === 'conclusion') {
        // conclusion类型放到末尾
        newOutline.nodes.push(selectedNode);
      } else {
        // abstract和summary类型放到开头
        newOutline.nodes.unshift(selectedNode);
      }
    } else {
      newOutline.nodes = [selectedNode];
    }
 
    // 更新outline状态
    setOutline(newOutline);
    setOutlineIsChange(true);
    
    // 根据type设置当前节点索引
    if (type === 'conclusion') {
      // conclusion类型设置为最后一个节点的索引
      setCurrentSectionIndex(newOutline.nodes.length - 1);
    } else {
      // abstract和summary类型设置为第一个节点
      setCurrentSectionIndex(0);
    }
    
    // 关闭模态框
    handleClose();
  };
  const lengthMap = [
    t("editor-editormodel-AI-RephraseLength-0"),
    t("editor-editormodel-AI-RephraseLength-1"),
    t("editor-editormodel-AI-RephraseLength-2"),
    t("editor-editormodel-AI-RephraseLength-3"),
    t("editor-editormodel-AI-RephraseLength-4"),
  ];

  // Degree options mapping - 使用与AiPanelContainer.tsx相同的翻译键
  const degreeMap = [
    t("editor-editormodel-AI-Rephrasedegree-0"),
    t("editor-editormodel-AI-Rephrasedegree-1"),
    t("editor-editormodel-AI-Rephrasedegree-2"),
    t("editor-editormodel-AI-Rephrasedegree-3"),
    t("editor-editormodel-AI-Rephrasedegree-4"),
  ];

  // 位置数组，与AiPanelContainer.tsx保持一致
  const lengthPositions = ["2rem", "5rem", "5.5rem", "9.8rem", "12rem"];
  const degreePositions = ["1.3rem", "3rem", "6rem", "9.5rem", "12rem"];

  // 状态管理 - 使用字符串而不是数字索引
  const [currentLength, setCurrentLength] = useState<string>(lengthMap[2]);
  const [currentDegreeValue, setCurrentDegreeValue] = useState<string>(
    degreeMap[2]
  );

  // 获取数据的函数 - 根据type调用不同的API
  const fetchData = async () => {
    if (!paperId) return;

    // 检查是否已经达到10条记录的限制
    if (carouselContent.length >= 10) {
      const typeText = type === 'abstract' ? '摘要' : type === 'summary' ? '总结' : '结论';
      toast({
        title: "",
        description: `最多只能生成10条${typeText}记录`,
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      let response;
      const requestData = {
        paperID: paperId,
        writingLevel: currentLength,
        writingLength: currentDegreeValue,
        userPrompt: summaryPrompt || undefined,
      };

      // 根据type调用不同的API
      switch (type) {
        case 'summary':
          response = await PaperService.generateSummary(requestData);
          break;
        case 'conclusion':
          response = await PaperService.generateConclusion(requestData);
          break;
        case 'abstract':
        default:
          response = await PaperService.generateAbstract(requestData);
          break;
      }

      if (response.node) {
        // 保存原始的response.node
        setOriginalNodes((prev) => [...prev, response.node]);
        const tiptapContent = convertOutlineToTiptapJson({
          ...response.node,
        });
        const newContent = { content: tiptapContent };
        setCarouselContent((prev) => {
          const updated = [...prev, newContent];
          // 设置当前显示为最新的内容
          setCurrentSlide(updated.length - 1);
          return updated;
        });
      }
    } catch (error) {
      console.error(`Failed to generate ${type}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  // 当模态框打开时获取初始数据
  useEffect(() => {
    if (isOpen && paperId && carouselContent.length === 0) {
      console.log('AutoGenModal opened, fetching initial data...');
      resetAllStates()
      fetchData();
    }
  }, [isOpen, paperId]);

  const handlePrevious = () => {
    if (isAnimating || carouselContent.length <= 1) return;
    
    setIsAnimating(true);
    setAnimationDirection('right');
    
    setTimeout(() => {
      setCurrentSlide((prev) =>
        prev === 0 ? carouselContent.length - 1 : prev - 1
      );
      setAnimationDirection('left');
      
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationDirection(null);
      }, 50);
    }, 150);
  };

  const handleNext = () => {
    if (isAnimating || carouselContent.length <= 1) return;
    
    setIsAnimating(true);
    setAnimationDirection('left');
    
    setTimeout(() => {
      setCurrentSlide((prev) =>
        prev === carouselContent.length - 1 ? 0 : prev + 1
      );
      setAnimationDirection('right');
      
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationDirection(null);
      }, 50);
    }, 150);
  };

  // 添加直接跳转到指定slide的函数，也支持动画
  const handleSlideChange = (targetIndex: number) => {
    if (isAnimating || targetIndex === currentSlide || carouselContent.length <= 1) return;
    
    const direction = targetIndex > currentSlide ? 'left' : 'right';
    
    setIsAnimating(true);
    setAnimationDirection(direction);
    
    setTimeout(() => {
      setCurrentSlide(targetIndex);
      setAnimationDirection(direction === 'left' ? 'right' : 'left');
      
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationDirection(null);
      }, 50);
    }, 150);
  };

  const handleRegenerateSummary = async () => {
    await fetchData();
  };

  // 重置所有状态到初始值
  const resetAllStates = () => {
    setSummaryPrompt("");
    setCarouselContent([]);
    setOriginalNodes([]);
    setCurrentSlide(0);
    setIsLoading(false);
    setIsAnimating(false);
    setAnimationDirection(null);
    setCurrentLength(lengthMap[2]);
    setCurrentDegreeValue(degreeMap[2]);
  };

  // 处理弹框关闭，先重置状态再关闭
  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  // 根据type获取对应的图标和标题
  const getTypeConfig = () => {
    switch (type) {
      case 'summary':
        return {
          icon: '/static/ToolAI/summary.svg',
          title: 'Summary',
          loadingText: 'Generating summary...',
          placeholder: 'Enter your summary requirements...'
        };
      case 'conclusion':
        return {
          icon: '/static/ToolAI/conclusion.svg',
          title: 'Conclusion',
          loadingText: 'Generating conclusion...',
          placeholder: 'Enter your conclusion requirements...'
        };
      case 'abstract':
      default:
        return {
          icon: '/static/ToolAI/abstract.svg',
          title: 'Abstract',
          loadingText: 'Generating abstract...',
          placeholder: 'Enter your abstract requirements...'
        };
    }
  };

  const typeConfig = getTypeConfig();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxW={pxToRem(850)}
        minW={pxToRem(850)}
        borderRadius="20px"
        bg="#FBFCFF"
        boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
      >
        <ModalHeader display="flex" alignItems="center" gap={2} pt={6} pb={2}>
          <Image src={typeConfig.icon} w="20px" h="20px" />
          <Text mb={0} fontSize="16px" fontWeight="500" color="black">
            {typeConfig.title}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Main content area */}
            {/* Carousel content with navigation buttons */}
            <HStack
              spacing={4}
              align="center"
              justify="space-between"
              height="120px"
            >
              {/* Content area */}
              <Box flex="1" height="120px" display="flex" alignItems="stretch">
                {isLoading ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    <Text fontSize="14px" color="gray.500" lineHeight="1.6">
                      {typeConfig.loadingText}
                    </Text>
                  </Box>
                ) : carouselContent.length > 0 ? (
                  <Box 
                    width="100%" 
                    height="120px" 
                    overflowY={"auto"}
                    position="relative"
                    sx={{
                      '& .ProseMirror': {
                        pointerEvents: 'none !important',
                        userSelect: 'none !important',
                        cursor: 'default !important',
                        outline: 'none !important',
                        border: 'none !important'
                      },
                      '& .ProseMirror *': {
                        pointerEvents: 'none !important',
                        userSelect: 'none !important',
                        cursor: 'default !important'
                      },
                      '& [contenteditable]': {
                        contentEditable: 'false !important',
                        pointerEvents: 'none !important',
                        userSelect: 'none !important'
                      }
                    }}
                  >
                    <Box
                      position="relative"
                      width="100%"
                      height="100%"
                      overflowY="auto"
                      overflowX="hidden"
                    >
                      <Box
                        position="absolute"
                        width="100%"
                        height="100%"
                        transform={
                          isAnimating && animationDirection === 'left'
                            ? 'translateX(-100%)'
                            : isAnimating && animationDirection === 'right'
                            ? 'translateX(100%)'
                            : 'translateX(0)'
                        }
                        transition="transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        opacity={isAnimating ? 0 : 1}
                        sx={{
                          transitionProperty: 'transform, opacity',
                          transitionDuration: '0.3s',
                          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        <BlockEditor
                          content={carouselContent[currentSlide].content}
                          readOnly={true}
                          isDisabled={true}
                          onCreate={() => {}}
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    <Text fontSize="14px" color="gray.500" lineHeight="1.6">
                      No abstract content available
                    </Text>
                  </Box>
                )}
              </Box>
            </HStack>

            {/* Navigation dots */}
            {carouselContent.length > 1 ? (
              <HStack justify="center" spacing={2}>
                {/* Left navigation button */}
                <Image
                  cursor={
                    carouselContent.length > 0 ? "pointer" : "not-allowed"
                  }
                  src={"/static/editor/left-icon.svg"}
                  onClick={
                    carouselContent.length > 0 ? handlePrevious : undefined
                  }
                  w="24px"
                  h="24px"
                  opacity={carouselContent.length > 0 ? 1 : 0.5}
                />
                {Array.from({ length: carouselContent.length }, (_, index) => (
                  <Box
                    key={index}
                    w="8px"
                    h="8px"
                    borderRadius="50%"
                    bg={index === currentSlide ? "#3898FF" : "gray.300"}
                    cursor={
                      carouselContent.length > 0 ? "pointer" : "not-allowed"
                    }
                    onClick={
                      carouselContent.length > 0
                        ? () => handleSlideChange(index)
                        : undefined
                    }
                    transition="background-color 0.2s"
                    opacity={carouselContent.length > 0 ? 1 : 0.5}
                    _hover={{
                      bg:
                        carouselContent.length > 0 && index === currentSlide
                          ? "#3898FF"
                          : carouselContent.length > 0
                          ? "gray.400"
                          : "gray.300",
                    }}
                  />
                ))}
                {/* Right navigation button */}
                <Image
                  cursor={
                    carouselContent.length > 0 ? "pointer" : "not-allowed"
                  }
                  src={"/static/editor/right-icon.svg"}
                  onClick={carouselContent.length > 0 ? handleNext : undefined}
                  w="24px"
                  h="24px"
                  opacity={carouselContent.length > 0 ? 1 : 0.5}
                />
              </HStack>
            ) : null}

            {/* Insert to the Article button */}
            <Button
              bg="#3898FF"
              color="white"
              borderRadius={pxToRem(8)}
              fontSize={pxToRem(16)}
              fontWeight="500"
              h={pxToRem(40)}
              w={pxToRem(200)}
              margin={"0 auto"}
              _hover={{ bg: "#2980FF" }}
              onClick={handleInsertToArticle}
              isDisabled={
                originalNodes.length === 0 ||
                currentSlide >= originalNodes.length
              }
            >
              Insert to the Article
            </Button>

            <Box h="1px" bg="#D3D7E2"></Box>

            {/* Control sections - 改为左右结构 */}
            <HStack spacing={6} align="start" justify="center">
              {/* Current Length Controller */}
              <VStack align="start" spacing={3}  mr={'150px'}>
                <Box
                  mt={2}
                  paddingLeft={
                    lengthPositions[lengthMap.indexOf(currentLength)]
                  }
                >
                  <Text
                    aria-multiline={false}
                    display="inline-block"
                    pl={1}
                    pr={1}
                    border={"1px solid #ccc"}
                    borderRadius={"3px"}
                    bg={"#FFFFFF"}
                    mb={0}
                  >
                    {currentLength}
                  </Text>
                </Box>
                <HStack
                  border={"1px solid #ccc"}
                  borderRadius={"0.5rem"}
                  paddingRight={1}
                  bg={"#FFFFFF"}
                  w={'17rem'}
                >
                  <Box
                    borderRight={"1px solid #ccc"}
                    borderRadius={"0.5rem"}
                    bg={"#FFFFFF"}
                  >
                    <Image
                      m={2}
                      w={pxToRem(24)}
                      objectFit="cover"
                      transform={"rotate(45deg)"}
                      src="/static/ToolAI/length.png"
                      alt="length"
                    />
                  </Box>
                  {lengthMap.map((item, index) => (
                    <IconButton
                      key={index.toString() + "|" + item}
                      bg={item == currentLength ? "#3898FF61" : "transparent"}
                      size={"sm"}
                      border={
                        item == currentLength ? "1px solid #3898FF" : undefined
                      }
                      borderRadius={"10px"}
                      icon={
                        <Image
                          w={item == currentLength ? pxToRem(24) : "6px"}
                          objectFit="cover"
                          transform={"rotate(45deg)"}
                          src={
                            "/static/ToolAI/" +
                            (item == currentLength ? "length.png" : "dot.png")
                          }
                        />
                      }
                      aria-label={""}
                      onClick={() => {
                        setCurrentLength(item);
                      }}
                    />
                  ))}
                </HStack>
              </VStack>

              {/* Current Level Controller */}
              <VStack align="start" spacing={3} w={'17rem'}>
                <Box mt={2} width={"100%"}>
                  <Text
                    aria-multiline={false}
                    display="inline-block"
                    pl={1}
                    pr={1}
                    mb={0}
                    border={"1px solid #ccc"}
                    bg={"#FFFFFF"}
                    borderRadius={"3px"}
                    whiteSpace={"nowrap"}
                    marginLeft={
                      degreePositions[degreeMap.indexOf(currentDegreeValue)]
                    }
                  >
                    {currentDegreeValue}
                  </Text>
                </Box>
                <HStack
                  border={"1px solid #ccc"}
                  borderRadius={"0.5rem"}
                  paddingRight={1}
                  bg={"#FFFFFF"}
                  w={'17rem'} 
                >
                  <Box
                    borderRight={"1px solid #ccc"}
                    borderRadius={"0.5rem"}
                    bg={"#FFFFFF"}
                  >
                    <Image
                      m={2}
                      w={pxToRem(24)}
                      objectFit="cover"
                      src="/static/ToolAI/degree.png"
                    />
                  </Box>
                  {degreeMap.map((item) => (
                    <IconButton
                      key={item}
                      bg={
                        item == currentDegreeValue ? "#3898FF61" : "transparent"
                      }
                      size={"sm"}
                      border={
                        item == currentDegreeValue
                          ? "1px solid #3898FF"
                          : undefined
                      }
                      borderRadius={"10px"}
                      icon={
                        <Image
                          w={item == currentDegreeValue ? pxToRem(24) : "6px"}
                          objectFit="cover"
                          src={
                            "/static/ToolAI/" +
                            (item == currentDegreeValue
                              ? "degree.png"
                              : "dot.png")
                          }
                        />
                      }
                      aria-label={""}
                      onClick={() => {
                        setCurrentDegreeValue(item);
                      }}
                    />
                  ))}
                </HStack>
              </VStack>
            </HStack>

            {/* Summary prompt input */}
            <Box textAlign={"center"}>
              <Textarea
                placeholder={typeConfig.placeholder}
                value={summaryPrompt}
                onChange={(e) => setSummaryPrompt(e.target.value)}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="12px"
                fontSize="14px"
                color="gray.500"
                minH="153px"
                resize="none"
                w="422px"
                margin={"0 auto"}
                _placeholder={{ color: "gray.400" }}
                _focus={{
                  borderColor: "#3898FF",
                  boxShadow: "0 0 0 1px #3898FF",
                }}
              />
            </Box>

            {/* Regenerate button */}
            <Button
              bg="#3898FF"
              color="white"
              borderRadius={pxToRem(8)}
              fontSize={pxToRem(16)}
              fontWeight="500"
              h={pxToRem(40)}
              w={pxToRem(160)}
              margin={"0 auto"}
              _hover={{ bg: "#2980FF" }}
              onClick={handleRegenerateSummary}
              isLoading={isLoading}
              loadingText="Generating..."
              isDisabled={!paperId}
            >
              Regenerate
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AutoGenModal;
